import { RefObject, useEffect, useState } from "react";

/**
 * Returns the nearest ancestor (up to `maxDepth` levels) that clips overflow (overflow: auto | scroll | hidden).
 * Used to determine the actual visible boundary for dropdown menu positioning.
 */
function getClippingParent(
  element: HTMLElement | null,
  maxDepth = 2,
  currentDepth = 0
): HTMLElement | null {
  if (!element || element === document.body || currentDepth >= maxDepth)
    return null;
  const { overflow, overflowY } = globalThis.getComputedStyle(element);
  if (/(auto|scroll|hidden)/.test(overflow + overflowY)) return element;
  return getClippingParent(element.parentElement, maxDepth, currentDepth + 1);
}

/**
 * useMenuPosition is a React hook that determines whether a dropdown menu should
 * be displayed below or above its trigger element, based on available viewport space.
 *
 * @param isOpen - Boolean indicating if the menu is open.
 * @param ref - RefObject of the trigger element (the element the menu is anchored to).
 * @param numItems - The number of items in the menu, used to estimate menu height.
 * @param itemHeight - The height (in pixels) of each menu item. Default is 40.
 * @param menuMaxHeight - The maximum height (in pixels) the menu can have. Default is 300.
 * @param menuPadding - The vertical padding (in pixels) to add to the menu height. Default is 16.
 * @returns {boolean} - Returns true if the menu should be displayed below (from top), false if above (from bottom).
 *
 * The hook recalculates on open state, content size, and trigger ref changes.
 * If there is not enough space below the trigger to fit the menu, but there is enough space above,
 * the menu will render above the trigger. Otherwise, it renders below.
 * Space is calculated relative to the nearest clipping ancestor (overflow: auto/scroll/hidden)
 * to correctly handle dropdowns inside scrollable or overflow-hidden containers.
 */
export function useMenuPosition(
  isOpen: boolean,
  ref: RefObject<HTMLElement>,
  numItems: number,
  itemHeight = 40,
  menuMaxHeight = 300,
  menuPadding = 16
) {
  const [showMenuFromTop, setShowMenuFromTop] = useState(true);

  useEffect(() => {
    if (isOpen && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const menuHeight = Math.min(
        menuMaxHeight,
        numItems * itemHeight + menuPadding
      );

      const clippingParent = getClippingParent(ref.current.parentElement);
      const clippingRect = clippingParent?.getBoundingClientRect();

      const maxBottom = clippingRect
        ? Math.min(window.innerHeight, clippingRect.bottom)
        : window.innerHeight;
      const minTop = clippingRect ? Math.max(0, clippingRect.top) : 0;

      const spaceBelow = maxBottom - rect.bottom;
      const spaceAbove = rect.top - minTop;

      if (spaceBelow < menuHeight && spaceAbove > menuHeight)
        setShowMenuFromTop(false);
      else setShowMenuFromTop(true);
    }
  }, [isOpen, numItems, itemHeight, menuMaxHeight, menuPadding, ref]);

  return showMenuFromTop;
}
